import Image from "next/image";

import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";

import { format } from "date-fns";
import { twMerge } from "tailwind-merge";

import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";

import FontAwesomeIcon from "components/FontAwesomeIcon";
import { roboto } from "utils/fonts";

type AddToCalendarButtonProps = {
  startTime: Date;
  endTime: Date;
  location?: string;
  name: string;
  details: string;
  onAddToCalendar?: (_type: string) => void;
};

const AddToCalendarButton = ({
  startTime,
  endTime,
  location,
  name,
  details,
  onAddToCalendar,
}: AddToCalendarButtonProps) => {
  const calendarOptions = [
    {
      icon: (
        <Image
          src="https://assets.smartkarma.com/events/google-calendar.png"
          alt="google-calendar"
          width={18}
          height={18}
        />
      ),
      text: "Google Calendar",
      onClick() {
        window.open(
          `https://calendar.google.com/calendar/u/0/r/eventedit?dates=${format(
            startTime,
            "yyyyMMdd'T'HHmmss"
          )}/${format(
            endTime,
            "yyyyMMdd'T'HHmmss"
          )}&details=${encodeURIComponent(details)}&&location=${
            location || ""
          }&text=${name}`,
          "_blank"
        );
        onAddToCalendar && onAddToCalendar("google");
      },
    },
    {
      icon: (
        <Image
          src="https://assets.smartkarma.com/events/outlook-calendar.png"
          alt="outlook-calendar"
          width={18}
          height={18}
        />
      ),
      text: "Outlook Live",
      onClick() {
        window.open(
          `https://outlook.live.com/calendar/0/deeplink/compose?body=${encodeURIComponent(
            details
          )}&enddt=${format(endTime, "yyyy-MM-dd'T'HH:mm:ss")}&location=${
            location || ""
          }&path=/calendar/action/compose&startdt=${format(
            startTime,
            "yyyy-MM-dd'T'HH:mm:ss"
          )}&subject=${name}`,
          "_blank"
        );
        onAddToCalendar && onAddToCalendar("outlook");
      },
    },
    {
      icon: (
        <Image
          src="https://assets.smartkarma.com/events/yahoo.png"
          alt="yahoo"
          width={18}
          height={18}
        />
      ),
      text: "Yahoo",
      onClick() {
        window.open(
          `https://calendar.yahoo.com/?desc${encodeURIComponent(
            details
          )}&et=${format(endTime, "yyyyMMdd'T'HHmmss")}&in_loc=${
            location || ""
          }&st=${format(startTime, "yyyyMMdd'T'HHmmss")}&title=${name}&v=60`,
          "_blank"
        );
        onAddToCalendar && onAddToCalendar("yahoo");
      },
    },
    {
      icon: (
        <Image
          src="https://assets.smartkarma.com/events/ics.svg"
          alt="ics"
          width={18}
          height={18}
        />
      ),
      text: "Download ICS",
      onClick() {
        import("ics").then((ics) => {
          ics.createEvent(
            {
              title: name,
              description: details,
              start: [
                startTime.getFullYear(),
                startTime.getMonth() + 1,
                startTime.getDate(),
                startTime.getHours(),
                startTime.getMinutes(),
              ],
              duration: { minutes: 60 },
            },
            (error, value) => {
              if (error) {
                console.log(error);
              }

              var blob = new Blob([value], { type: "text/calendar" });
              const a = document.createElement("a");
              document.body.appendChild(a);
              const blobUrl = window.URL.createObjectURL(blob);
              a.href = blobUrl;
              a.download = `event.ics`;
              a.click();
              setTimeout(() => {
                window.URL.revokeObjectURL(blobUrl);
                document.body.removeChild(a);
              }, 0);
            }
          );
          onAddToCalendar && onAddToCalendar("ics");
        });
      },
    },
  ];

  return (
    <OverlayTrigger
      placement={"bottom"}
      trigger={"click"}
      rootClose
      overlay={
        <Popover className={twMerge(roboto.variable, "font-roboto")}>
          <Popover.Body className={"p-0"}>
            <div className={""}>
              <ul className="list-none p-0 m-0 text-[15px] leading-[18px] py-[5px]">
                {calendarOptions.map(({ icon, text, onClick }) => {
                  return (
                    <li
                      key={text}
                      onClick={(e) => {
                        e.stopPropagation();
                        onClick();
                      }}
                      className="flex space-x-[5px] p-[10px] cursor-pointer hover:bg-black/10"
                    >
                      {icon} <div>{text}</div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </Popover.Body>
        </Popover>
      }
    >
      <div
        className={twMerge(
          "inline-block",
          "text-smart-teal hover:text-dark-teal"
        )}
      >
        <FontAwesomeIcon icon={faPlus} /> Add To Calendar
      </div>
    </OverlayTrigger>
  );
};

export default AddToCalendarButton;
